import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { userService } from "../../services/UserService";
import useScreenSize from "../../hooks/UseScreenSize";
import ForgotPasswordDialog from "./ForgotPasswordDialog";
import MessageToast from "../common/MessageToast";
import {
  Building,
  Layers,
  Server,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";
import { Eye } from "lucide-react";
import { EyeOff } from "lucide-react";
import NotificationToast from "../common/NotificationToast";
import { useLoader } from "../../context/LoaderContext";
import { AppConfig } from "../../utils/Constants";
import { useTheme } from "../../context/ThemeContext";

const AuthComponent: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const navigate = useNavigate();
  const { isLoggedIn, login } = useAuth();
  const isMobile = useScreenSize();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const { showLoader, hideLoader } = useLoader();
  const { theme } = useTheme();
  const [toast, setToast] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const showToast = (type: "success" | "error", message: string) => {
    setToast({ type, message });
  };

  const handleNavigateToLandingPage = () => {
    navigate("/auth");
  };

  useEffect(() => {
    if (isLoggedIn) {
      navigate("/");
    }
  }, [isLoggedIn, navigate]);

  const handleTabChange = (newTabIndex: number) => {
    setTabIndex(newTabIndex);
    setRegistrationSuccess(false);
  };

  const handleLogin = async () => {
    if (!email || !password) {
      showToast("error", "Email and password are required.");
      return;
    }
    setLoading(true);
    showLoader();
    try {
      const { data, message } = await userService.loginUser({
        email,
        password,
      });

      login();
      showToast("success", message || "Logged in successfully!");
      hideLoader();

      navigate("/");
    } catch (error: any) {
      showToast("error", error.message || "Login failed. Please try again.");
    } finally {
      hideLoader();
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!firstName || !lastName || !email || !password) {
      showToast("error", "All fields are required.");
      return;
    }

    if (!termsAccepted) {
      showToast("error", "You must accept the terms and conditions.");
      return;
    }

    setLoading(true);
    showLoader();
    try {
      const { message } = await userService.registerUser({
        firstName,
        lastName,
        email,
        password,
      });

      setRegistrationSuccess(true);
      showToast("success", message || "Registration successful!");

      setFirstName("");
      setLastName("");
      setEmail("");
      setPassword("");
      setTermsAccepted(false);
      hideLoader();

      setTabIndex(0);
    } catch (error: any) {
      showToast(
        "error",
        error.message || "Registration failed. Please try again."
      );
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  const handleForgotPasswordSubmit = async (email: string) => {
    showLoader();
    try {
      await userService.requestPasswordReset(email);
      showToast("success", "Password reset email sent.");
      hideLoader();
    } catch (error: any) {
      showToast("error", `Failed to send password reset email.`);
      hideLoader();
    }
  };

  return (
    <div className="flex h-screen w-screen bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark font-sans relative overflow-hidden auth-page">
      {/* Subtle grid pattern */}
      <div className="subtle-grid" style={{ display: "block" }}></div>

      <div className="absolute top-6 left-6 z-20">
        <button
          onClick={handleNavigateToLandingPage}
          className="flex items-center gap-2.5 group transition-all duration-200"
          aria-label="Go to Landing Page"
        >
          <div className="relative flex items-center">
            <div className="absolute inset-0 bg-secondary/5 dark:bg-secondary/10 rounded-lg blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
            <div
              className={`relative flex items-center justify-center rounded-lg p-2 transition-all duration-200 ${
                theme === "light"
                  ? "bg-background-panel border border-divider/50 group-hover:border-divider group-hover:bg-background-default"
                  : "bg-background-paperDark/50 border border-divider-dark/30 group-hover:border-secondary/30 group-hover:bg-background-paperDark/70"
              }`}
            >
              <img
                src={
                  theme === "light"
                    ? "https://cdn.forgeworks.ai/assets/forgeworks-logo-black.png"
                    : AppConfig.LOGO_URL
                }
                alt={`${AppConfig.PRODUCT_NAME_SHORT} Logo`}
                className="w-6 h-6 object-contain transition-transform duration-200 group-hover:scale-105"
              />
            </div>
          </div>
          <div className="flex flex-col items-start">
            <span
              className={`text-base font-semibold tracking-tight leading-tight transition-colors duration-200 ${
                theme === "dark" ? "text-white" : "text-text-primary"
              }`}
            >
              {AppConfig.PRODUCT_NAME_SHORT}
            </span>
            <span
              className={`text-[9px] font-medium tracking-wider uppercase leading-none mt-0.5 transition-colors duration-200 ${
                theme === "dark"
                  ? "text-text-secondaryDark"
                  : "text-text-secondary"
              }`}
            >
              TOOLS
            </span>
          </div>
        </button>
      </div>

      <div className="flex flex-col items-center justify-center w-full md:w-1/2 px-8 bg-background-default/90 dark:bg-background-dark/90 backdrop-blur-[0.5px] relative z-10">
        {isMobile && (
          <img
            src={AppConfig.LOGO_URL}
            alt="Forgeworks Logo"
            className="mb-8 w-16"
          />
        )}

        <div className="w-full max-w-md">
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-text-primary dark:text-text-primaryDark mb-2 tracking-tight">
              {tabIndex === 0 ? "Welcome back" : "Create an account"}
            </h1>
            <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
              {tabIndex === 0
                ? "Sign in to your Forgeworks account"
                : "Get started with Forgeworks"}
            </p>
          </div>

          <div className="flex mb-6 border-b border-divider dark:border-divider-dark">
            <button
              onClick={() => handleTabChange(0)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                tabIndex === 0
                  ? "text-text-primary dark:text-text-primaryDark border-b-2 border-text-primary dark:border-text-primaryDark -mb-[1px]"
                  : "text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark"
              }`}
            >
              Log In
            </button>
            <button
              onClick={() => handleTabChange(1)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                tabIndex === 1
                  ? "text-text-primary dark:text-text-primaryDark border-b-2 border-text-primary dark:border-text-primaryDark -mb-[1px]"
                  : "text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark"
              }`}
            >
              Sign Up
            </button>
          </div>

          <form
            onSubmit={(e) => e.preventDefault()}
            className="flex flex-col space-y-4"
          >
            {tabIndex === 1 && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-1.5 uppercase tracking-wide">
                    First Name
                  </label>
                  <input
                    type="text"
                    placeholder="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-divider dark:border-divider-dark rounded-lg bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark placeholder:text-text-secondary dark:placeholder:text-text-secondaryDark focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary transition"
                    autoComplete="given-name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-1.5 uppercase tracking-wide">
                    Last Name
                  </label>
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-divider dark:border-divider-dark rounded-lg bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark placeholder:text-text-secondary dark:placeholder:text-text-secondaryDark focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary transition"
                    autoComplete="family-name"
                  />
                </div>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-1.5 uppercase tracking-wide">
                Email
              </label>
              <input
                type="email"
                placeholder="gsd@forgeworks.ai"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-divider dark:border-divider-dark rounded-lg bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark placeholder:text-text-secondary dark:placeholder:text-text-secondaryDark focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary transition"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-1.5 uppercase tracking-wide">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 pr-10 text-sm border border-divider dark:border-divider-dark rounded-lg bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark placeholder:text-text-secondary dark:placeholder:text-text-secondaryDark focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary transition"
                  autoComplete={
                    tabIndex === 0 ? "current-password" : "new-password"
                  }
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark transition-colors"
                >
                  {showPassword ? (
                    <Eye className="w-4 h-4" />
                  ) : (
                    <EyeOff className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
            {tabIndex === 0 && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setForgotPasswordOpen(true)}
                  className="text-xs text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark transition-colors"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {tabIndex === 1 && (
              <div className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  id="terms"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-0.5 h-4 w-4 border border-divider dark:border-divider-dark rounded bg-background-default dark:bg-background-dark text-secondary focus:ring-2 focus:ring-secondary/20 focus:ring-offset-0"
                />
                <label
                  htmlFor="terms"
                  className="text-xs text-text-secondary dark:text-text-secondaryDark leading-relaxed"
                >
                  I agree to the{" "}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-text-primary dark:text-text-primaryDark hover:underline font-medium"
                  >
                    terms and conditions
                  </a>
                </label>
              </div>
            )}

            <button
              type="submit"
              onClick={tabIndex === 0 ? handleLogin : handleRegister}
              disabled={
                loading ||
                (tabIndex === 0 && (!email || !password)) ||
                (tabIndex === 1 && !termsAccepted)
              }
              className={`w-full h-11 px-4 rounded-lg text-sm font-semibold transition ${
                loading ||
                (tabIndex === 0 && (!email || !password)) ||
                (tabIndex === 1 && !termsAccepted)
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              } ${
                tabIndex === 0
                  ? "bg-secondary hover:bg-secondary-hover text-white"
                  : "bg-secondary hover:bg-secondary-hover dark:bg-background-paperDark dark:hover:bg-background-panelDark dark:border dark:border-secondary/30 dark:hover:border-secondary/50 text-white dark:text-secondary"
              }`}
            >
              {loading
                ? tabIndex === 0
                  ? "Signing in..."
                  : "Creating account..."
                : tabIndex === 0
                ? "Sign in"
                : "Create account"}
            </button>
          </form>

          {registrationSuccess && (
            <NotificationToast
              header="Registration: Pending"
              subtext="Check your email to verify your account."
            />
          )}
        </div>
      </div>

      {/* RIGHT SIDE START */}

      <div className="hidden md:flex flex-col justify-between w-1/2 bg-background-panel/90 dark:bg-background-panelDark/90 backdrop-blur-[0.5px] text-text-primary dark:text-text-primaryDark px-16 py-16 relative overflow-hidden z-10">
        {/* Subtle blended background design */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/12 dark:bg-secondary/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-secondary/10 dark:bg-secondary/12 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-secondary/6 dark:bg-secondary/6 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10">
          <h2 className="text-3xl font-semibold mb-3 text-text-primary dark:text-text-primaryDark tracking-tight">
            Forgeworks
            <span className="text-secondary">.ai</span>
          </h2>
          <p className="text-base text-text-primary dark:text-text-primaryDark mb-4 leading-relaxed font-medium">
            A RevOps automation platform and marketplace designed to connect,
            understand and act across your revenue systems.
          </p>
          <p className="text-sm text-text-secondary dark:text-text-secondaryDark leading-relaxed mb-10">
            Modern revenue teams rely on dozens of tools: CRMs, billing
            platforms, analytics and workflow systems, but critical revenue data
            often lives in silos. Forgeworks bridges those systems, creates a
            unified revenue picture and powers intelligent agents that monitor,
            explain and optimize your RevOps stack.
          </p>
        </div>

        <div className="space-y-5 relative z-10">
          <div className="flex items-start gap-3">
            <div className="bg-background-default dark:bg-background-paperDark p-2 rounded-lg flex-shrink-0 border border-divider dark:border-divider-dark">
              <Building className="w-4 h-4 text-text-primary dark:text-text-primaryDark" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-text-primary dark:text-text-primaryDark mb-1">
                The Forgeworks Marketplace
              </h3>
              <p className="text-xs text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                A growing collection of RevOps products, from data bridges to
                fully agentic workflows, that install into your organization and
                operate across your connected systems.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="bg-background-default dark:bg-background-paperDark p-2 rounded-lg flex-shrink-0 border border-divider dark:border-divider-dark">
              <TrendingUp className="w-4 h-4 text-text-primary dark:text-text-primaryDark" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-text-primary dark:text-text-primaryDark mb-1">
                Revenue Bridge
              </h3>
              <p className="text-xs text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                Connect HubSpot and Stripe to bring accurate revenue metrics
                (MRR, ARR, subscription status) directly into your CRM, enabling
                reporting and automation based on real billing data.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="bg-background-default dark:bg-background-paperDark p-2 rounded-lg flex-shrink-0 border border-divider dark:border-divider-dark">
              <Layers className="w-4 h-4 text-text-primary dark:text-text-primaryDark" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-text-primary dark:text-text-primaryDark mb-1">
                Secure Multi-Tenant Platform
              </h3>
              <p className="text-xs text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                Built for organizations to connect their tools, configure
                products from the marketplace and deploy RevOps solutions
                without custom engineering.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="bg-background-default dark:bg-background-paperDark p-2 rounded-lg flex-shrink-0 border border-divider dark:border-divider-dark">
              <ShieldCheck className="w-4 h-4 text-text-primary dark:text-text-primaryDark" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-text-primary dark:text-text-primaryDark mb-1">
                Built for What Comes Next
              </h3>
              <p className="text-xs text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                Today, Forgeworks helps teams unify revenue data. Tomorrow, it
                will help teams understand and operate their entire revenue
                engine with AI agents.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-divider dark:border-divider-dark relative z-10">
          <p className="text-xs text-text-secondary dark:text-text-secondaryDark">
            &copy; {new Date().getFullYear()} {AppConfig.PRODUCT_NAME}
          </p>
        </div>
      </div>

      {/* RIGHT SIDE END */}

      <ForgotPasswordDialog
        open={forgotPasswordOpen}
        onClose={() => setForgotPasswordOpen(false)}
        onSubmit={handleForgotPasswordSubmit}
      />
      {toast && (
        <MessageToast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default AuthComponent;
